import React, { useEffect, useState } from "react";
import { Button, Iconography, IconographySizes } from "@hid-galaxy-ui/galaxy-react";
import "./GraphDashboardList.scss";
import ReportCategories from "./report-categories";
import ReportsList from "./reports-list";
import CategoryModal from "./report-category-modal";
import ReportEmpty from "./report-empty";
import ErrorPopup from "./error-popup";
import { axiosRestInstance } from "../../../../common/axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Loader, LoaderSizes, ThickNessLevel } from "@hid-galaxy-ui/galaxy-react";
import ReportsDetailPage from "./report-detail";
import {
  useSessionCanEditModule,
  useSessionModuleReadOnly,
} from "../../../../hooks/useSessionPermissions";
import { REPORTS_DASHBOARD_PERMISSION_KEYS as RD_KEYS } from "../../../../utils/permissions/reportsDashboardPermissionKeys";
import { canEditCategoryByRole } from "../../../../utils/permissions/categoryAccess";
import { useAppSelector } from "../../../../redux";

type SelectedCategory = {
  key: string;
  title: string;
  description: string;
  user_view?: unknown;
  user_edit?: unknown;
};

export const createCategoryAPI = async (payload: any): Promise<any> => {
  try {
    const response = await axiosRestInstance.post("/category", payload);
    return response.data;
  } catch (error: unknown) {
    const err = error as {
      response?: {
        data?: {
          message?: string;
        };
      };
    };

    return {
      success: false,
      message:
        err?.response?.data?.message ||
        "Category name already exists", // Default message for duplicate category
    };
  }
};

// Type for ref methods exposed from ReportsList
export type ReportsListHandle = {
  openDeleteModal: () => void;
  enterSelectionMode: () => void;
  exitSelectionMode: () => void;
  selectedCount: number;
};

// Loader component
const PageLoader: React.FC = () => (
  <div className="gdl-page-loader">
    <Loader variant={LoaderSizes.large} />
  </div>
);

const ReportsDashboard: React.FC = () => {
  const { categoryKey } = useParams<{ categoryKey: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedCategory, setSelectedCategory] = useState<SelectedCategory | null>(null);
  const [isCreateOpen, setCreateOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [categoriesCount, setCategoriesCount] = useState<number | null>(null);
  const [loadingCategory, setLoadingCategory] = useState<boolean>(!!categoryKey);
  const [loadingCategories, setLoadingCategories] = useState<boolean>(false); // NEW LOADING STATE FOR CATEGORIES
  const [reportError, setReportError] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);
  const reportsListRef = React.useRef<ReportsListHandle | null>(null);
  const [deletableReportsCount, setDeletableReportsCount] = useState(0);
  const { loggedUserDetail } = useAppSelector((state) => state.auth);
  /** Super admin (role_id 1) may delete SNAPSHOT reports; others may not. */
  const canDeleteSnapshotReports = Number(loggedUserDetail?.role_id) === 1;
  const categoryManagementEdit = useSessionCanEditModule(
    RD_KEYS.CATEGORY_MANAGEMENT
  );
  const categoryManagementReadOnly = useSessionModuleReadOnly(
    RD_KEYS.CATEGORY_MANAGEMENT
  );

  // Check if the query param `mode=add` exists in the URL
  const isAddReportMode = new URLSearchParams(location.search).get("mode") === "add";

  // NEW STATE: error popup
  const [popup, setPopup] = useState({
    open: false,
    message: "",
    type: "error" as "success" | "error",
  });

  // Fetch categories when navigating to the list page
  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await axiosRestInstance.get("/category/all");
      //console.log("categry data", response);
      setCategoriesCount(response.data.length);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategoriesCount(0);
    } finally {
      setLoadingCategories(false); // ALWAYS reset
    }
  };

  useEffect(() => {
    if (!categoryKey) {
      setSelectedCategory(null);
      setLoadingCategory(false);

      // FETCH categories immediately when coming to listing page
      fetchCategories();
      return;
    }
    else {
      const fetchCategory = async () => {
        setLoadingCategory(true);
        try {
          const response = await axiosRestInstance.get(`/category/${categoryKey}`);
          const fetchedCategory: SelectedCategory = {
            title: response.data.data.name || "Untitled Category",
            description: response.data.data.description || "",
            key: String(response.data.data.ID),
            user_view: response.data.data.user_view,
            user_edit: response.data.data.user_edit,
          };
          setSelectedCategory(fetchedCategory);
        } catch (error: any) {
          if (error.response?.status === 404) {
            navigate("/hid-admin/dashboard/reports-dashboard");
          } else {
            console.error("Failed to fetch category:", error);
          }
        } finally {
          setLoadingCategory(false);
        }
      };

      fetchCategory();
    }
    
  }, [categoryKey]);

  const headerTitle = selectedCategory ? selectedCategory.title + " Reports" : "Dashboard";
  const headerDescription = selectedCategory
    ? selectedCategory.description
    : "Generate and download partner program reports";

  const isReportsListPage = !!categoryKey;
  const buttonLabel = isReportsListPage ? "Add Report" : "Create Category";
  const showRequestsButton = categoriesCount !== null && categoriesCount > 0;

  const categoryLevelEdit = selectedCategory
    ? canEditCategoryByRole(loggedUserDetail?.role, selectedCategory.user_edit)
    : true;
  const canShowCategoryReportActions = categoryManagementEdit && categoryLevelEdit;

  // Check if any loading state is true
  const isLoading = loadingCategory || loadingCategories;

  if (isLoading) {
    return <PageLoader />; // Show loader if any data is loading
  }

  const onAddReport = () => {
    if (!selectedCategory) return;
    navigate(
      `/hid-admin/dashboard/reports-dashboard/category/${selectedCategory.key}?mode=add`,
      {
        state: {
          category: selectedCategory,
        },
      }
    );
  };

  const onOpenCategory = (category: SelectedCategory) => {
    navigate(`/hid-admin/dashboard/reports-dashboard/category/${category.key}`);
    setSelectedCategory(category);
  };

  return (
    <>
      {/* NEW ERROR POPUP */}
      <ErrorPopup
        open={popup.open}
        message={popup.message}
        type={popup.type}
        onClose={() => {
          setPopup({ open: false, message: "", type: "error" });
          // Close the Category Modal when the popup is shown
          setCreateOpen(false); // Close the modal here
        }}
      />

      <div className="gdl-root gdl-reports__container">
        {/* Breadcrumbs */}
        {categoryKey && selectedCategory && (
          <nav className="gdl-breadcrumbs">
            <button
              className="gdl-breadcrumbs__link"
              title="Go to Dashboard"
              onClick={() => {
                setSelectedCategory(null);
                navigate("/hid-admin/dashboard/reports-dashboard");
              }}
            >
              Dashboard
            </button>
            <Iconography icon="angleRight" size={IconographySizes.Small} />
            <button
              className="gdl-breadcrumbs__link"
              onClick={() => navigate(`/hid-admin/dashboard/reports-dashboard/category/${selectedCategory.key}`)}
            >
              {selectedCategory.title} Reports
            </button>
            {isAddReportMode && (
              <>
                <Iconography icon="angleRight" size={IconographySizes.Small} />
                <span className="gdl-breadcrumbs__current">Add Report</span>
              </>
            )}
          </nav>
        )}

        {/* Header */}
        {!isAddReportMode && (
          <header className="gdl-header">
            <div className="gdl-header__text">
              <h1 className="gdl-title">{headerTitle}</h1>
              <p className="gdl-subtitle">{headerDescription}</p>
            </div>

            <div className="gdl-header__actions">
              {/* Remove Reports when the user can delete at least one report in this list */}
              {isReportsListPage &&
                canShowCategoryReportActions &&
                deletableReportsCount > 0 &&
                !selectionMode && (
                <Button
                  label={
                    <span className="btn-with-icon">
                      Remove Reports
                      <Iconography icon="trashCan" size={IconographySizes.Small} style={{ color: '#0057B7', width: '18px', height: '18px' }}/>
                    </span>
                  }
                  variant="secondary"
                  onClick={() => {
                    setSelectionMode(true);
                    reportsListRef.current?.enterSelectionMode();
                  }}
                />
              )}

              {/* Show Cancel + Remove Selected Items buttons ONLY when in selection mode */}
              {canShowCategoryReportActions &&
                selectionMode &&
                deletableReportsCount > 0 && (
                <>
                  <Button
                    label="Cancel"
                    variant="secondary"
                    onClick={() => {
                      setSelectionMode(false);
                      reportsListRef.current?.exitSelectionMode();
                    }}
                  />
                  <Button
                    label="Remove Selected Items"
                    variant="contained"
                    color="error"
                    disabled={selectedCount === 0}
                    onClick={() => reportsListRef.current?.openDeleteModal()}
                  />
                </>
              )}

              {/* Requests Button */}
              {canShowCategoryReportActions && showRequestsButton && !selectionMode && (
                <Button
                  label={
                    <span className="btn-with-icon">
                      Requests for Reports
                      <span className="badge">4</span>
                    </span>
                  }
                  variant="secondary"
                  color="primary"
                />
              )}

              {/* Add Report (category list) or Create Category (dashboard root) */}
              {(isReportsListPage ? canShowCategoryReportActions : categoryManagementEdit) &&
                !(isReportsListPage && (selectionMode || reportError)) &&
                (isReportsListPage ? (
                  <Button
                    label={
                      <span className="btn-with-icon">
                        {buttonLabel}
                        <Iconography icon="plus" size={IconographySizes.Small} />
                      </span>
                    }
                    variant="contained"
                    color="primary"
                    title={buttonLabel}
                    onClick={onAddReport}
                  />
                ) : (
                  <Button
                    label={
                      <span className="btn-with-icon">
                        {buttonLabel}
                        <Iconography
                          icon="plus"
                          size={IconographySizes.Small}
                        />
                      </span>
                    }
                    variant="contained"
                    color="primary"
                    title={buttonLabel}
                    onClick={() => setCreateOpen(true)}
                  />
                ))}
            </div>
          </header>
        )}

        {/* Main Content */}
        {!categoryKey ? (
          categoriesCount === 0 ? (
            <ReportEmpty
              type="category"
              showButton={categoryManagementEdit}
              onAction={() => setCreateOpen(true)}
            />          
          ) : (
            <ReportCategories
              key={refreshKey}
              selectedCategoryKey={categoryKey}
              onCategoriesLoaded={(count) => setCategoriesCount(count)}
              onOpenCategory={onOpenCategory}
              categoryManagementEdit={categoryManagementEdit}
              categoryManagementReadOnly={categoryManagementReadOnly}
            />
          )
        ) : selectedCategory ? (
          isAddReportMode ? (
            <ReportsDetailPage />
        ) : (
          <ReportsList
            ref={reportsListRef} categories={[{ key: categoryKey, title: selectedCategory.title, description: selectedCategory.description, iconKey: categoryKey, count: 0}]}
            onAction={onAddReport} reportError={reportError} setReportError={setReportError}
            onDeletableReportsLoaded={(count: number) =>
              setDeletableReportsCount(count)
            }
            onSelectionChange={(count) => setSelectedCount(count)}
            onExitSelectionMode={() => setSelectionMode(false)}
            showReportActionButton={canShowCategoryReportActions}
            canDeleteSnapshotReports={canDeleteSnapshotReports}
            />
        )
        ) : (
          <div>Loading...</div>
        )}

        {/* CREATE CATEGORY MODAL */}
        <CategoryModal
          mode="create"
          open={isCreateOpen}
          readOnly={categoryManagementReadOnly}
          onClose={() => setCreateOpen(false)}
          onSubmit={async (data) => {
            if (!categoryManagementEdit) return;
            const payload = {
              name: data.name,
              description: data.description,
              user_view: data.viewOnlyRoles,
              user_edit: data.editRoles,
            };

            const result = await createCategoryAPI(payload);

            if (!result?.success) {
              setPopup({
                open: true,
                message: result.message,
                type: "error",
              });
              setCreateOpen(false); // Close the modal
              return;
            }
            
            fetchCategories();
            setCreateOpen(false);
            setRefreshKey((prev) => prev + 1);
            setPopup({
              open: true,
              message: "Category created successfully.",
              type: "success",
            });
          }}
        />
      </div>
    </>
  );
};

export default ReportsDashboard;
